import { useCallback } from 'react';

import {
  getAppliedLabels,
  getLabelFit,
  getLabelWidth,
  getOffsetY,
  getRectCenter,
} from './utils/canvas-label-utils.js';
import { ELLIPSIS_CHARACTER } from '../../../../typography/utils.js';
import { useCanvas } from '../../../core/components/canvas/hooks/useCanvas.js';
import { getCanvasColor } from '../../../core/components/canvas/utils/canvas-color.js';
import {
  charsFontWidth12pixelsSize400Map,
  charsFontWidth12pixelsSize500Map,
} from '../../../core/components/text-ellipsis/chars-font-width-map.js';
import {
  calculateWidth,
  truncateString,
} from '../../../core/components/text-ellipsis/truncate-string.js';
import { getColorByContrast } from '../../../core/utils/color-contrast.js';
import { getFontDetails } from '../../../core/utils/get-font-details.js';
import {
  DEFAULT_CLUSTER_LABEL_FONTSIZE,
  DEFAULT_CLUSTER_LABEL_LINE_HEIGHT,
  DEFAULT_FONT_WEIGHT,
  DEFAULT_LABEL_ALIGN,
  DEFAULT_LABEL_BASELINE,
  DEFAULT_LABEL_FONTSIZE,
  DEFAULT_LABEL_LINE_HEIGHT,
  DEFAULT_LABEL_PADDING,
  INCREASED_FONT_WEIGHT,
  LABEL_OPTIONS,
  RECT_BORDER_RADIUS,
} from '../../constants.js';
import { useTruncationMode } from '../../hooks/useTruncationMode.js';

interface TreeMapSquareRendererProps {
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  opacity: number;
  name: string;
  value: string;
  skipDrawing?: boolean;
  withLabel?: boolean;
  withValueOnly?: boolean;
}

const textAlign = DEFAULT_LABEL_ALIGN;
const textBaseline = DEFAULT_LABEL_BASELINE;
const fontSize = DEFAULT_LABEL_FONTSIZE;
const fontWeight = DEFAULT_FONT_WEIGHT;
const padding = DEFAULT_LABEL_PADDING;
const lineHeight = DEFAULT_LABEL_LINE_HEIGHT;
const boldFontWeight = INCREASED_FONT_WEIGHT;
const clusterFontSize = DEFAULT_CLUSTER_LABEL_FONTSIZE;
const clusterLineHeight = DEFAULT_CLUSTER_LABEL_LINE_HEIGHT;

export function TreeMapSquareRenderer(props: TreeMapSquareRendererProps) {
  const {
    x,
    y,
    width,
    height,
    color,
    opacity,
    name,
    value,
    skipDrawing = false,
    withLabel = false,
    withValueOnly = false,
  } = props;

  const truncationMode = useTruncationMode();

  const draw = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      if (skipDrawing) {
        return false;
      }
      if (withLabel) {
        ctx.textAlign = textAlign;
        ctx.textBaseline = textBaseline;
      }
      ctx.moveTo(x, y);
      ctx.beginPath();
      ctx.globalAlpha = opacity;

      ctx.fillStyle = getCanvasColor(color);
      ctx.filter = 'none';
      ctx.roundRect(x, y, width, height, RECT_BORDER_RADIUS);
      ctx.fill();

      if (withLabel && withValueOnly) {
        ctx.fillStyle = getColorByContrast(ctx.fillStyle);
        ctx.font = getFontDetails(
          boldFontWeight,
          clusterFontSize,
          clusterLineHeight,
        );

        const maxWidth = width - padding * 2;
        const minHeight = padding * 2 + clusterFontSize;
        const { centerX, centerY } = getRectCenter(width, height);

        if (height >= minHeight && maxWidth > 0) {
          const truncatedValue = truncateString(
            value,
            maxWidth,
            charsFontWidth12pixelsSize500Map,
            truncationMode,
          );
          ctx.fillText(truncatedValue, x + centerX, y + centerY);
        }
      }

      if (withLabel && !withValueOnly) {
        ctx.fillStyle = getColorByContrast(ctx.fillStyle);

        ctx.font = getFontDetails(fontWeight, fontSize, lineHeight);
        const nameWidth = calculateWidth(
          name,
          charsFontWidth12pixelsSize400Map,
        );

        ctx.font = getFontDetails(
          boldFontWeight,
          clusterFontSize,
          clusterLineHeight,
        );

        const labelWidth = getLabelWidth(
          nameWidth,
          value,
          charsFontWidth12pixelsSize500Map,
        );
        const labelFit = getLabelFit(
          height,
          width,
          fontSize,
          padding,
          labelWidth,
        );

        const withEllipsis = labelFit === LABEL_OPTIONS.Ellipsis;
        const maxWidth = width - padding;

        const truncatedName = withEllipsis
          ? truncateString(
              name,
              maxWidth,
              charsFontWidth12pixelsSize400Map,
              truncationMode,
              'start',
            )
          : name;

        const displayedName =
          truncatedName === ELLIPSIS_CHARACTER ? '' : truncatedName;

        const labels = getAppliedLabels(labelFit, displayedName, value);

        const { centerX, centerY } = getRectCenter(width, height);

        labels.forEach((label: string, i: number, array) => {
          // increaded - bold text for the 'value'
          if (i === 1) {
            ctx.font = getFontDetails(
              boldFontWeight,
              clusterFontSize,
              clusterLineHeight,
            );
          } else {
            // regular text for the 'name'
            ctx.font = getFontDetails(fontWeight, fontSize, lineHeight);
          }
          const offsetY = getOffsetY(array, i, fontSize);
          ctx.fillText(label, x + centerX, y + centerY + offsetY);
        });
      }

      return false;
    },
    [
      color,
      height,
      name,
      opacity,
      skipDrawing,
      truncationMode,
      value,
      width,
      withLabel,
      withValueOnly,
      x,
      y,
    ],
  );

  useCanvas('treemap-square', draw);

  return null;
}

TreeMapSquareRenderer['displayName'] = 'TreeMapSquareRenderer';
